/**
 * Canvas-based multi-page PDF viewer for certificate modals.
 * Zoom is CSS-driven (pinch / double-tap) so ending a gesture never
 * clears and re-renders pages mid-frame (no flicker / layout jump).
 */
(function (global) {
  'use strict';

  var PDFJS_VERSION = '4.10.38';
  var PDFJS_URL =
    'https://cdn.jsdelivr.net/npm/pdfjs-dist@' + PDFJS_VERSION + '/build/pdf.min.mjs';
  var WORKER_URL =
    'https://cdn.jsdelivr.net/npm/pdfjs-dist@' + PDFJS_VERSION + '/build/pdf.worker.min.mjs';

  var MIN_ZOOM = 1;
  var MAX_ZOOM = 4;
  var ZOOM_STEP = 0.25;
  var DOUBLE_TAP_MS = 280;
  var DOUBLE_TAP_ZOOM = 2;
  /** Render sharpness budget: crisp through ~2× zoom on retina. */
  var RENDER_QUALITY = 2;

  var pdfjsLoadPromise = null;
  var sessions = new WeakMap();

  function loadPdfJs() {
    if (pdfjsLoadPromise) return pdfjsLoadPromise;
    pdfjsLoadPromise = import(PDFJS_URL).then(function (pdfjsLib) {
      pdfjsLib.GlobalWorkerOptions.workerSrc = WORKER_URL;
      return pdfjsLib;
    });
    return pdfjsLoadPromise;
  }

  function normalizeUrl(url) {
    if (!url) return url;
    try {
      var parsed = new URL(url, global.location.href);
      if (parsed.protocol === 'http:') parsed.protocol = 'https:';
      parsed.hash = '';
      return parsed.toString();
    } catch (err) {
      var normalized = url.indexOf('//') === 0 ? 'https:' + url : url;
      var hashIdx = normalized.indexOf('#');
      return hashIdx >= 0 ? normalized.slice(0, hashIdx) : normalized;
    }
  }

  function clampZoom(zoom) {
    var z = Number(zoom);
    if (!isFinite(z)) z = 1;
    return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.round(z * 100) / 100));
  }

  function clearContainer(container) {
    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }
  }

  function getSession(container) {
    return sessions.get(container) || null;
  }

  function syncScrollOverflow(container, zoom) {
    if (zoom > 1.01) {
      container.classList.add('bk-media-pdf-scroll--zoomed');
    } else {
      container.classList.remove('bk-media-pdf-scroll--zoomed');
    }
  }

  function destroySession(container) {
    var session = sessions.get(container);
    if (!session) return;
    session.cancelled = true;
    if (session.resizeObserver) {
      try {
        session.resizeObserver.disconnect();
      } catch (e) {}
      session.resizeObserver = null;
    }
    if (session.pinchCleanup) {
      try {
        session.pinchCleanup();
      } catch (e) {}
      session.pinchCleanup = null;
    }
    if (session.pdfDocument) {
      try {
        session.pdfDocument.destroy();
      } catch (e) {}
      session.pdfDocument = null;
    }
    sessions.delete(container);
    clearContainer(container);
    container.classList.remove('bk-media-pdf-scroll--zoomed');
  }

  function measureBaseWidth(container) {
    var width = container.clientWidth || container.offsetWidth || 0;
    if (width < 40) {
      var area = container.closest
        ? container.closest('.bk-media-pdf-viewer-area')
        : null;
      if (area) width = area.clientWidth || area.offsetWidth || 0;
      else if (container.parentElement) {
        width = container.parentElement.clientWidth || 0;
      }
    }
    return Math.max(260, width - 24);
  }

  /** Apply zoom via transform + sizer (layout matches visuals, no re-render). */
  function applyZoom(session, container, zoom, focalX, focalY) {
    var next = clampZoom(zoom);
    var prev = session.zoom || 1;
    var sizer = container.querySelector('.bk-media-pdf-zoom-sizer');
    var pages = container.querySelector('.bk-media-pdf-pages');
    if (!sizer || !pages || !session.contentWidth || !session.contentHeight) {
      session.zoom = next;
      return;
    }

    var rect = container.getBoundingClientRect();
    var fx = focalX != null ? focalX : rect.width / 2;
    var fy = focalY != null ? focalY : rect.height / 2;

    var contentX = container.scrollLeft + fx;
    var contentY = container.scrollTop + fy;

    session.zoom = next;
    sizer.style.width = session.contentWidth * next + 'px';
    sizer.style.height = session.contentHeight * next + 'px';
    pages.style.transform = 'scale(' + next + ')';
    pages.style.transformOrigin = '0 0';

    syncScrollOverflow(container, next);

    if (prev > 0) {
      var ratio = next / prev;
      container.scrollLeft = Math.max(0, contentX * ratio - fx);
      container.scrollTop = Math.max(0, contentY * ratio - fy);
    }
  }

  async function renderAllPages(pdf, container, session) {
    var dpr = Math.min(global.devicePixelRatio || 1, 2);
    session.baseWidth = measureBaseWidth(container);
    var layoutWidth = session.baseWidth;
    var renderWidth = layoutWidth * RENDER_QUALITY;

    var keepZoom = session.zoom || 1;
    var keepScrollLeft = container.scrollLeft;
    var keepScrollTop = container.scrollTop;

    clearContainer(container);

    var sizer = document.createElement('div');
    sizer.className = 'bk-media-pdf-zoom-sizer';

    var pagesWrap = document.createElement('div');
    pagesWrap.className = 'bk-media-pdf-pages';

    sizer.appendChild(pagesWrap);
    container.appendChild(sizer);

    for (var pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      if (session.cancelled) return;

      var page = await pdf.getPage(pageNum);
      if (session.cancelled) return;

      var baseViewport = page.getViewport({ scale: 1 });
      var renderScale = renderWidth / baseViewport.width;
      var viewport = page.getViewport({ scale: renderScale });
      var layoutScale = layoutWidth / baseViewport.width;
      var layoutViewport = page.getViewport({ scale: layoutScale });

      var pageWrap = document.createElement('div');
      pageWrap.className = 'bk-media-pdf-page';
      pageWrap.setAttribute('data-page', String(pageNum));

      var canvas = document.createElement('canvas');
      var context = canvas.getContext('2d', { alpha: false });
      canvas.width = Math.floor(viewport.width * dpr);
      canvas.height = Math.floor(viewport.height * dpr);
      // Displayed at fit-width; zoom scales the pages wrapper
      canvas.style.width = Math.floor(layoutViewport.width) + 'px';
      canvas.style.height = Math.floor(layoutViewport.height) + 'px';
      canvas.setAttribute('aria-label', 'Seite ' + pageNum + ' von ' + pdf.numPages);

      pageWrap.appendChild(canvas);
      pagesWrap.appendChild(pageWrap);

      var transform = dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : null;
      await page.render({
        canvasContext: context,
        viewport: viewport,
        transform: transform,
      }).promise;
    }

    if (session.cancelled) return;

    session.contentWidth = pagesWrap.offsetWidth || layoutWidth;
    session.contentHeight = pagesWrap.offsetHeight || 1;

    // Reset zoom bookkeeping then re-apply so sizer/transform match
    session.zoom = 1;
    applyZoom(session, container, keepZoom, null, null);
    container.scrollLeft = keepScrollLeft;
    container.scrollTop = keepScrollTop;
  }

  function setZoom(container, zoom, options) {
    options = options || {};
    var session = getSession(container);
    if (!session || session.cancelled) {
      return Promise.resolve(null);
    }
    applyZoom(session, container, zoom, options.focalX, options.focalY);
    return Promise.resolve({ zoom: session.zoom });
  }

  function setupGestures(container, session) {
    var pinch = null;
    var lastTap = { time: 0, x: 0, y: 0 };

    function touchDistance(t1, t2) {
      var dx = t1.clientX - t2.clientX;
      var dy = t1.clientY - t2.clientY;
      return Math.sqrt(dx * dx + dy * dy);
    }

    function onTouchStart(e) {
      if (e.touches.length === 2) {
        var t0 = e.touches[0];
        var t1 = e.touches[1];
        var rect = container.getBoundingClientRect();
        pinch = {
          startDist: touchDistance(t0, t1) || 1,
          startZoom: session.zoom,
          focalX: (t0.clientX + t1.clientX) / 2 - rect.left,
          focalY: (t0.clientY + t1.clientY) / 2 - rect.top,
        };
        e.preventDefault();
        return;
      }

      if (e.touches.length === 1) {
        var t = e.touches[0];
        var now = Date.now();
        var dx = Math.abs(t.clientX - lastTap.x);
        var dy = Math.abs(t.clientY - lastTap.y);
        if (now - lastTap.time < DOUBLE_TAP_MS && dx < 24 && dy < 24) {
          e.preventDefault();
          var rect = container.getBoundingClientRect();
          var target =
            session.zoom < DOUBLE_TAP_ZOOM - 0.05 ? DOUBLE_TAP_ZOOM : MIN_ZOOM;
          setZoom(container, target, {
            focalX: t.clientX - rect.left,
            focalY: t.clientY - rect.top,
          });
          lastTap.time = 0;
        } else {
          lastTap.time = now;
          lastTap.x = t.clientX;
          lastTap.y = t.clientY;
        }
      }
    }

    function onTouchMove(e) {
      if (!pinch || e.touches.length !== 2) return;
      e.preventDefault();
      var rect = container.getBoundingClientRect();
      var dist = touchDistance(e.touches[0], e.touches[1]) || 1;
      var liveZoom = clampZoom(pinch.startZoom * (dist / pinch.startDist));
      pinch.focalX = (e.touches[0].clientX + e.touches[1].clientX) / 2 - rect.left;
      pinch.focalY = (e.touches[0].clientY + e.touches[1].clientY) / 2 - rect.top;
      applyZoom(session, container, liveZoom, pinch.focalX, pinch.focalY);
    }

    function endPinch() {
      // Zoom already applied live — nothing to swap or re-render.
      pinch = null;
    }

    function onTouchEnd(e) {
      if (pinch && e.touches.length < 2) endPinch();
    }

    function onWheel(e) {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      var rect = container.getBoundingClientRect();
      var direction = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
      setZoom(container, session.zoom + direction, {
        focalX: e.clientX - rect.left,
        focalY: e.clientY - rect.top,
      });
    }

    container.addEventListener('touchstart', onTouchStart, { passive: false });
    container.addEventListener('touchmove', onTouchMove, { passive: false });
    container.addEventListener('touchend', onTouchEnd, { passive: true });
    container.addEventListener('touchcancel', onTouchEnd, { passive: true });
    container.addEventListener('wheel', onWheel, { passive: false });

    session.pinchCleanup = function () {
      container.removeEventListener('touchstart', onTouchStart);
      container.removeEventListener('touchmove', onTouchMove);
      container.removeEventListener('touchend', onTouchEnd);
      container.removeEventListener('touchcancel', onTouchEnd);
      container.removeEventListener('wheel', onWheel);
    };
  }

  function mount(url, container, options) {
    options = options || {};
    if (!container) {
      return Promise.reject(new Error('Missing PDF container'));
    }

    destroySession(container);

    var session = {
      cancelled: false,
      pdfDocument: null,
      resizeObserver: null,
      pinchCleanup: null,
      url: normalizeUrl(url),
      zoom: 1,
      baseWidth: 0,
      contentWidth: 0,
      contentHeight: 0,
    };
    sessions.set(container, session);

    return loadPdfJs()
      .then(function (pdfjsLib) {
        if (session.cancelled) return null;
        return pdfjsLib.getDocument({
          url: session.url,
          withCredentials: false,
          isEvalSupported: false,
        }).promise;
      })
      .then(function (pdf) {
        if (!pdf || session.cancelled) return { numPages: 0, zoom: 1 };
        session.pdfDocument = pdf;
        return renderAllPages(pdf, container, session).then(function () {
          if (session.cancelled) return { numPages: pdf.numPages, zoom: session.zoom };

          setupGestures(container, session);

          if (typeof ResizeObserver !== 'undefined') {
            var resizeTimer = null;
            var lastWidth = session.baseWidth;
            session.resizeObserver = new ResizeObserver(function () {
              if (session.cancelled || !session.pdfDocument) return;
              clearTimeout(resizeTimer);
              resizeTimer = setTimeout(function () {
                if (session.cancelled || !session.pdfDocument) return;
                var nextBase = measureBaseWidth(container);
                if (Math.abs(nextBase - lastWidth) < 8) return;
                lastWidth = nextBase;
                renderAllPages(session.pdfDocument, container, session).catch(function () {});
              }, 180);
            });
            session.resizeObserver.observe(container);
          }

          return { numPages: pdf.numPages, zoom: session.zoom };
        });
      });
  }

  function unmount(container) {
    if (container) destroySession(container);
  }

  function zoomIn(container) {
    var session = getSession(container);
    if (!session) return Promise.resolve(null);
    return setZoom(container, session.zoom + ZOOM_STEP);
  }

  function zoomOut(container) {
    var session = getSession(container);
    if (!session) return Promise.resolve(null);
    return setZoom(container, session.zoom - ZOOM_STEP);
  }

  function resetZoom(container) {
    return setZoom(container, 1);
  }

  function getZoom(container) {
    var session = getSession(container);
    return session ? session.zoom : 1;
  }

  global.BkMediaPdfViewer = {
    mount: mount,
    unmount: unmount,
    setZoom: setZoom,
    zoomIn: zoomIn,
    zoomOut: zoomOut,
    resetZoom: resetZoom,
    getZoom: getZoom,
    normalizeUrl: normalizeUrl,
    loadPdfJs: loadPdfJs,
    MIN_ZOOM: MIN_ZOOM,
    MAX_ZOOM: MAX_ZOOM,
    ZOOM_STEP: ZOOM_STEP,
  };
})(typeof window !== 'undefined' ? window : this);
